import React, { useState } from 'react'
import { Divider } from '@fs/zion-ui'
import { Link, useLocation } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'
import { i18n } from '@fs/zion-locale'
import { css } from '@emotion/core'
import { boolAttr } from '@fs/zion-frontend-friends'
import Subnav from '@fs/tree-subnav-legacy'

const navBarCss = css`
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
  white-space: nowrap;
  scroll-behavior: smooth;
  line-height: 2.5rem;
  margin-left: 10px;
`

const spanCss = css`
  font-family: Verdana, Ayuthaya, sans-serif;
  color: #000000;
  text-decoration: none;
  background: transparent;
  border-radius: 4px;
  border: 0;
  cursor: pointer;
  font-size: 14px;
  margin-right: 5px;
  padding: 4px 10px;
  vertical-align: middle;

  [lang|='zh'] &,
  [lang|='ja'] &,
  [lang|='ko'] & {
    font-size: 16px;
  }

  &:hover {
    background: #e1edc1;
    text-decoration: underline; /* required or else the <a> tag will underline with a color */
  }
  &:focus {
    outline: none;
  }
  &[is-selected] {
    background: #e1edc1;
  }
`
const untranslatedBooksLangs = [
  'zh',
  'zh-hans',
  'ja',
  'ko',
  'th',
  'da',
  'hu',
  'nl',
  'pl',
  'sv',
  'sk',
  'fi',
  'mn',
  'bg',
  'km',
  'id',
  'ro',
  'sm',
  'to',
  'vi',
  'fj',
  'sq',
  'cs',
  'no',
]

function NavLink({ path, name, external, index, selectedIndex, setSelectedIndex }) {
  return (
    <Link to={path} external={external} onClick={() => setSelectedIndex(index)}>
      <span css={spanCss} index={index} is-selected={boolAttr(selectedIndex === index)}>
        {name}
      </span>
    </Link>
  )
}

export default function NavigationTabs() {
  const { pathname } = useLocation()

  if (pathname.includes('tree')) {
    return <Subnav page="find" />
  }

  return <NavigationTabsInner />
}

function NavigationTabsInner() {
  const { t } = useTranslation()
  // Books and Wiki are hidden for CJK languages at the request of FS CJK specialists
  const hideBooksLangs = untranslatedBooksLangs.indexOf(i18n.language) >= 0
  const { pathname } = useLocation()
  const getDefaultIndex = () => {
    // this array helps us find out which nav link is selected or highlighted
    const pages = [
      '/collection',
      '/images',
      // Although the path that this nav link goes to is "/tree/find/name" or "/tree/name" we want all of the "/tree" pages (ex: "/tree/id") to highlight the Family Tree link so that is why this isn't the exact pathname
      '/tree',
      '/genealogies',
      '/catalog',
      '/library/books',
      t('search.tab.wikiLink', '/wiki/en/Main_Page'),
    ]
    const startIndex = pages.findIndex((page) => pathname.includes(page))
    return startIndex > 0 ? startIndex : 0
  }
  const [selectedIndex, setSelectedIndex] = useState(getDefaultIndex())

  function navLink({ path, name, index, external = true }) {
    return (
      <NavLink
        path={path}
        name={name}
        index={index}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        external={external}
      />
    )
  }

  return (
    <>
      <nav css={navBarCss}>
        {navLink({ path: '/', name: t('search.tab.records', 'Records'), index: 0, external: false })}
        {navLink({ path: '/records/images', name: t('search.tab.images', 'Images'), index: 1 })}
        {navLink({
          path: '/tree/name',
          name: t('search.tab.tree', 'Family Tree'),
          index: 2,
          external: false,
        })}
        {navLink({
          path: '/genealogies',
          name: t('search.tab.genealogies', 'Genealogies'),
          index: 3,
          external: false,
        })}
        {navLink({ path: '/search/catalog', name: t('search.tab.catalog', 'Catalog'), index: 4 })}
        {!hideBooksLangs && navLink({ path: '/library/books', name: t('search.tab.books', 'Books'), index: 5 })}
        {!hideBooksLangs &&
          navLink({
            path: t('search.tab.wikiLink', '/wiki/en/Main_Page'),
            name: t('search.tab.wiki', 'Wiki'),
            index: 6,
          })}
      </nav>
      <Divider />
    </>
  )
}
